// src/pages/Admin/ReglementInscription/EditReglementInscriptionModal.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { updateReglementInscription } from "../../../services/reglementinscriptionServices";
import { getSettings } from "../../../services/settingsServices";
import {
  getAllDossiersInscription,
  updateDossierInscription,
} from "../../../services/dossierServices";

/* Helpers */
const fmtEUR = (n) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

function neighborsFromSeason(season) {
  const m = /^(\d{4})\s*-\s*(\d{4})$/.exec(season || "");
  if (!m) return [season].filter(Boolean);
  const y1 = parseInt(m[1], 10);
  const y2 = parseInt(m[2], 10);
  return [`${y1 - 1}-${y2 - 1}`, `${y1}-${y2}`, `${y1 + 1}-${y2 + 1}`];
}

const EditReglementInscriptionModal = ({
  isModalOpen,
  setIsModalOpen, // conservé pour compat
  fetchReglements,
  reglement,
  onClose,
}) => {
  const adherent = reglement?.Adherent || {};
  const nomPrenom =
    [adherent?.nom, adherent?.prenom].filter(Boolean).join(" ") || "—";

  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    id: reglement?.id,
    saison: reglement?.saison || "",
    montant_lic_assurance: reglement?.montant_lic_assurance ?? "",
    montant_cotisation: reglement?.montant_cotisation ?? "",
    reduction_foyer: reglement?.reduction_foyer ?? "",
    pass_sport: reglement?.pass_sport ?? "",
    reduction_saison: reglement?.reduction_saison ?? "",
    type_reglement: reglement?.type_reglement || "",
    nb_reglement: reglement?.nb_reglement ?? "",
    observations: reglement?.observations || "",
    statut_reglement: reglement?.statut_reglement || "",
  });

  // Sync à chaque changement de reglement reçu
  useEffect(() => {
    setForm({
      id: reglement?.id,
      saison: reglement?.saison || "",
      montant_lic_assurance: reglement?.montant_lic_assurance ?? "",
      montant_cotisation: reglement?.montant_cotisation ?? "",
      reduction_foyer: reglement?.reduction_foyer ?? "",
      pass_sport: reglement?.pass_sport ?? "",
      reduction_saison: reglement?.reduction_saison ?? "",
      type_reglement: reglement?.type_reglement || "",
      nb_reglement: reglement?.nb_reglement ?? "",
      observations: reglement?.observations || "",
      statut_reglement: reglement?.statut_reglement || "",
    });
  }, [reglement]);

  const loadSeasons = useCallback(async () => {
    setLoading(true);
    try {
      const settings = await getSettings();
      const active = settings?.[0]?.saison_active || form.saison || "";
      const around = neighborsFromSeason(active);
      const opts = Array.from(
        new Set([...(form.saison ? [form.saison] : []), ...around])
      ).filter(Boolean);
      setSeasons(opts);
    } catch (e) {
      console.error(e);
      toast.error("Impossible de charger les paramètres (saison).");
    } finally {
      setLoading(false);
    }
  }, [form.saison]);

  useEffect(() => {
    if (isModalOpen) loadSeasons();
  }, [isModalOpen, loadSeasons]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const liveTotal = useMemo(() => {
    const n = (v) => Number(String(v).replace(",", ".")) || 0;
    return (
      n(form.montant_lic_assurance) +
      n(form.montant_cotisation) +
      n(form.reduction_foyer) +
      n(form.pass_sport) +
      n(form.reduction_saison)
    );
  }, [form]);

  const canSubmit = useMemo(() => {
    return (
      !loading &&
      !submitting &&
      String(form.id || "").trim() !== "" &&
      String(form.saison || "").trim() !== "" &&
      String(form.statut_reglement || "").trim() !== ""
    );
  }, [form, loading, submitting]);

  // 🔗 Synchronisation Dossier.validate_reglement après édition
  const syncDossierValidateReglement = useCallback(
    async (adherentId, saison, statut) => {
      try {
        const validated =
          statut === "Réglé" || statut === "Partiellement réglé";

        const dossiers = await getAllDossiersInscription();
        const arr = Array.isArray(dossiers) ? dossiers : [];

        // On cherche par (adherentId + saison)
        const target = arr.find((d) => {
          const aId = d?.inscription_adherent_id || d?.Adherent?.id;
          const s = d?.saison || d?.Adherent?.saison_active;
          return String(aId) === String(adherentId) && String(s) === String(saison);
        });

        if (!target) {
          console.warn(
            "[EditReglement] Aucun dossier trouvé pour adherent=",
            adherentId,
            " saison=",
            saison
          );
          return; // on ne bloque pas la MAJ si pas de dossier
        }

        await updateDossierInscription(target.id, {
          validate_reglement: validated,
        });
      } catch (e) {
        console.error(
          "[EditReglement] Erreur synchronisation dossier.validate_reglement:",
          e
        );
        toast.error(
          "Règlement mis à jour, mais la synchronisation du dossier a échoué."
        );
      }
    },
    []
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const num = (v) =>
        v === "" || v === null || v === undefined ? null : Number(v);

      const payload = {
        id: form.id,
        saison: form.saison,
        montant_lic_assurance: num(form.montant_lic_assurance),
        montant_cotisation: num(form.montant_cotisation),
        reduction_foyer: num(form.reduction_foyer),
        pass_sport: num(form.pass_sport),
        reduction_saison: num(form.reduction_saison),
        type_reglement: form.type_reglement || "",
        nb_reglement:
          form.nb_reglement === "" ? null : Number(form.nb_reglement),
        observations: form.observations || "",
        statut_reglement: form.statut_reglement,
      };

      await updateReglementInscription(payload);

      // ⬇️ Synchronisation auto du dossier après édition
      const adherentId = reglement?.Adherent?.id || reglement?.adherent_id;
      if (adherentId && payload.saison) {
        await syncDossierValidateReglement(
          adherentId,
          payload.saison,
          payload.statut_reglement
        );
      }

      toast.success("Règlement mis à jour avec succès.");
      fetchReglements?.();
      onClose?.();
    } catch (e) {
      console.error("Erreur mise à jour règlement", e);
      toast.error("Erreur lors de la mise à jour du règlement.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* overlay */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      {/* conteneur */}
      <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
        {/* header dégradé avec Nom/Prénom & Saison */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 text-white">
          <h3 className="text-lg sm:text-xl font-bold text-center uppercase">
            Modifier un règlement d’inscription
          </h3>
          <p className="text-center text-sm mt-1">
            {nomPrenom} {form.saison ? <span className="opacity-90">— Saison {form.saison}</span> : null}
          </p>
        </div>

        {/* contenu scrollable + barre d’actions sticky */}
        <form onSubmit={handleSubmit} className="relative">
          <div className="p-5 sm:p-6 space-y-6 max-h-[80vh] overflow-y-auto">
            {/* Bloc : Saison */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-white/70 dark:bg-slate-900/40">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
                Saison
              </h4>
              <select
                name="saison"
                value={form.saison}
                onChange={handleChange}
                disabled={loading}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                required
              >
                <option value="">
                  {loading ? "Chargement…" : "— Sélectionner —"}
                </option>
                {seasons.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            {/* Bloc : Montants */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-white/70 dark:bg-slate-900/40">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
                Montants
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Licence & Assurance (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="montant_lic_assurance"
                    value={form.montant_lic_assurance}
                    onChange={handleChange}
                    placeholder="0,00"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Cotisation (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="montant_cotisation"
                    value={form.montant_cotisation}
                    onChange={handleChange}
                    placeholder="0,00"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Réduction foyer (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="reduction_foyer"
                    value={form.reduction_foyer}
                    onChange={handleChange}
                    placeholder="ex. -20,00"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Pass’Sport (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="pass_sport"
                    value={form.pass_sport}
                    onChange={handleChange}
                    placeholder="ex. -70,00"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Réduction saison (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="reduction_saison"
                    value={form.reduction_saison}
                    onChange={handleChange}
                    placeholder="ex. -10,00"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
              </div>

              <div className="mt-3 text-sm">
                Total provisoire :{" "}
                <span className="font-semibold">{fmtEUR(liveTotal)}</span>
              </div>
            </div>

            {/* Bloc : Règlement */}
            <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-white/70 dark:bg-slate-900/40">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">
                Mode & Statut du règlement
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Type de règlement
                  </label>
                  <select
                    name="type_reglement"
                    value={form.type_reglement}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  >
                    <option value="">— Sélectionner —</option>
                    <option value="En ligne">En ligne</option>
                    <option value="Chèque">Chèque</option>
                    <option value="Espèces">Espèces</option>
                    <option value="Virement">Virement</option>
                    <option value="CB">CB</option>
                    <option value="HelloAsso">HelloAsso</option>
                    <option value="Chèques Vacances">Chèques Vacances</option>
                    <option value="Coupons Sports">Coupons Sports</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Nombre de règlements
                  </label>
                  <input
                    type="number"
                    name="nb_reglement"
                    value={form.nb_reglement}
                    onChange={handleChange}
                    placeholder="ex. 1, 2, 3…"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Statut
                  </label>
                  <select
                    name="statut_reglement"
                    value={form.statut_reglement}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                    required
                  >
                    <option value="">— Sélectionner —</option>
                    <option value="Réglé">Réglé</option>
                    <option value="Partiellement réglé">Partiellement réglé</option>
                    <option value="En cours">En cours</option>
                    <option value="Non réglé">Non réglé</option>
                    <option value="Problème">Problème</option>
                    <option value="Annulé">Annulé</option>
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium mb-1">
                    Observations / N° Pass’Sport
                  </label>
                  <textarea
                    name="observations"
                    value={form.observations}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Notes libres…"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Barre d’actions sticky */}
          <div className="sticky bottom-0 -mx-5 sm:-mx-6 bg-white/90 dark:bg-slate-900/90 backdrop-blur border-t border-slate-200 dark:border-slate-800 px-5 sm:px-6 py-3 flex justify-end gap-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text={submitting ? "Enregistrement…" : "Mettre à jour"}
              icon={FaSave}
              type="submit"
              disabled={!canSubmit}
              className={`!rounded-xl gap-2 ${
                !canSubmit ? "opacity-60 pointer-events-none" : ""
              }`}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditReglementInscriptionModal;
